// ./src/components/features/Artists/EventArtistBookingDrawer/EventArtistBookingDrawer.tsx
//
// Create/edit drawer for an EventArtist (a booked act on an event's lineup).
// Self-loading in edit mode; no success toast. Picking a catalog act copies its
// name (the BE snapshots name/photo/deal server-side on create). An ad-hoc act is
// just a typed name with no catalog link. Money terms, booking status + milestones,
// party size and document attachments live in the collapsible sections below.
// Mirrors EventPartnerEditDrawer.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  NumberInput,
  Group,
  Divider,
  Skeleton,
  Checkbox,
  Input,
  Box,
  Switch,
  Button,
  Text,
  SimpleGrid,
  Card,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import {
  Artist,
  EventArtist,
  EventArtistFile,
  EventArtistInput,
  EventArtistFileInput,
  ARTIST_BILLING_VALUES,
  ARTIST_BILLING_LABELS,
  ARTIST_BOOKING_STATUS,
  ARTIST_BOOKING_STATUS_VALUES,
  ARTIST_BOOKING_STATUS_LABELS,
  EVENT_ARTIST_DEAL_TYPE,
  EVENT_ARTIST_DEAL_TYPE_VALUES,
  EVENT_ARTIST_DEAL_TYPE_LABELS,
  ARTIST_DEAL_TYPES_WITH_PERCENTAGE,
  EventGuestService,
  BudgetItem,
  listAllPages,
  EventTimeSlot,
  EventMapElement,
  EventMapElementService,
} from "@/utils/api";
import { getFormErrors, formatErrors, formatCurrency } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalEventArtistsContext } from "@/contexts/EventArtistsContext";
import { useCurrentEvent } from "@/contexts";
import { AppDrawer, AppIcon, CatalogCurrencyWarning, MediaImageField, MediaImageRef, SelectField } from "@/components/generic";
import { MoneyInput } from "@/components/features/Budget/MoneyInput/MoneyInput";
import { BudgetPaymentsDrawer } from "@/components/features/Budget/BudgetPaymentsDrawer/BudgetPaymentsDrawer";
import { ArtistSelect } from "../ArtistSelect/ArtistSelect";
import { EventArtistFilesEditor } from "../EventArtistFilesEditor/EventArtistFilesEditor";
import { TouringPartyDrawer } from "../TouringPartyDrawer/TouringPartyDrawer";
import { EventArtistDatesEditor } from "../EventArtistDatesEditor/EventArtistDatesEditor";
import { PerformanceRow, performanceRowsOf, setInstants } from "./performanceRules";

export interface EventArtistBookingDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  recordId?: string | null;
  onCreated?: (a: EventArtist) => void;
  onUpdated?: (a: EventArtist) => void;
  /**
   * #165 round 2 — the event's dates / sessions: the drawer then edits which of them the act plays and its set on each.
   * Omitted (the wizard, before the schedule exists): no such section, and the dates are left as they are.
   */
  dates?: EventTimeSlot[];
  /** The event's zone — set times are entered in it. */
  timezone?: string | null;
  isRecurring?: boolean;
  /** A NEW booking made from a date on the Artists page starts on that date. */
  defaultSlotId?: string | null;
}

interface FormData {
  artistId: string | null;
  name: string;
  photo: MediaImageRef | null;
  blurb: string;
  billing: string;
  billingIndex: number | "";
  bookingStatus: string;
  holdRank: number | "";
  holdExpiresAt: string | null;
  contractSigned: boolean;
  depositPaid: boolean;
  advanceComplete: boolean;
  dealType: string;
  amount: number | null;
  percentage: number | "";
  depositAmount: number | null;
  trackInBudget: boolean;
  entourageCount: number | "";
  notes: string;
  files: EventArtistFile[];
  performances: PerformanceRow[];
  status: number;
}

// ⚠ Only list a name something here READS. A listed key with nowhere to land parks the server's refusal in
// `fields`, where nothing renders it and the save fails with no message on screen.
const KNOWN_FORM_FIELDS = [
  "mainImageUploadedFileId",
  "artistId", "name", "blurb", "billing", "billingIndex", "bookingStatus",
  "holdRank", "holdExpiresAt", "contractSigned", "depositPaid", "advanceComplete",
  "dealType", "amount", "percentage", "depositAmount", "trackInBudget",
  "entourageCount", "notes", "files", "status", "performances",
];

const getInitialFormData = (defaultSlotId?: string | null): FormData => ({
  artistId: null,
  name: "",
  photo: null,
  blurb: "",
  billing: "",
  billingIndex: "",
  bookingStatus: ARTIST_BOOKING_STATUS.INQUIRY,
  holdRank: "",
  holdExpiresAt: null,
  contractSigned: false,
  depositPaid: false,
  advanceComplete: false,
  dealType: EVENT_ARTIST_DEAL_TYPE.NONE,
  amount: null,
  percentage: "",
  depositAmount: null,
  trackInBudget: true,
  entourageCount: "",
  notes: "",
  files: [],
  performances: defaultSlotId ? [{ eventTimeSlotId: defaultSlotId, start: "", end: "", mapElementId: "" }] : [],
  status: STATUSES.ACTIVE,
});

const fromRecord = (r: EventArtist, tz: string | null): FormData => ({
  artistId: r.artistId || null,
  name: r.name || "",
  photo: r.mainImageUploadedFileId
    ? { uploadedFileId: r.mainImageUploadedFileId, url: r.mainImageUploadedFile?.fileUrl ?? null }
    : null,
  blurb: r.blurb || "",
  billing: r.billing || "",
  billingIndex: r.billingIndex ?? "",
  bookingStatus: r.bookingStatus || ARTIST_BOOKING_STATUS.INQUIRY,
  holdRank: r.holdRank ?? "",
  holdExpiresAt: r.holdExpiresAt ? r.holdExpiresAt.slice(0, 10) : null,
  contractSigned: !!r.contractSigned,
  depositPaid: !!r.depositPaid,
  advanceComplete: !!r.advanceComplete,
  dealType: r.dealType || EVENT_ARTIST_DEAL_TYPE.NONE,
  amount: r.amount ?? null,
  percentage: r.percentage ?? "",
  depositAmount: r.depositAmount ?? null,
  trackInBudget: r.trackInBudget ?? true,
  entourageCount: r.entourageCount ?? "",
  notes: r.notes || "",
  files: r.files ? r.files.map((f) => ({ ...f })) : [],
  performances: performanceRowsOf(r.performances, tz),
  status: r.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const EditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={80} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const EventArtistBookingDrawer: React.FC<EventArtistBookingDrawerProps> = ({
  opened,
  onClose,
  eventId,
  recordId,
  onCreated,
  onUpdated,
  dates,
  timezone = null,
  isRecurring = false,
  defaultSlotId = null,
}) => {
  const { t } = useTranslation(["artists", "common"]);
  const withDates = Array.isArray(dates) && dates.length > 0;

  // #165 round 2 — the event map's elements, as the stages a set can be on (read whole: a picker's options).
  const [stages, setStages] = useState<Array<{ value: string; label: string }>>([]);
  useEffect(() => {
    if (!opened || !withDates) {
      return undefined;
    }

    let alive = true;

    listAllPages<EventMapElement>(EventMapElementService.list, { eventId: [eventId] }).then((res) => {
      if (alive && res.didSucceed() && Array.isArray(res.data)) {
        setStages(
          (res.data as EventMapElement[])
            .filter((e) => !!e.id)
            .map((e) => ({ value: e.id as string, label: (e.name ?? "").trim() || "—" }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
      }
    });

    return () => {
      alive = false;
    };
  }, [opened, withDates, eventId]);
  // #A4.7a — every amount on the booking is in the EVENT's currency.
  const eventCurrency = useCurrentEvent().currentEvent?.currencyCode ?? null;

  const ctx = useOptionalEventArtistsContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  const [record, setRecord] = useState<EventArtist | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  // The linked budget line, kept in SEPARATE state from `record` so refreshing the
  // paid/remaining after a payment change does NOT re-seed the form (which keys on
  // `record`) and clobber unsaved edits.
  const [budgetItem, setBudgetItem] = useState<BudgetItem | null>(null);

  // The stacked budget payments drawer (opened from the Money section for a tracked,
  // saved booking). While it's open we gate this drawer's ⌘S shortcut.
  const [paymentsOpen, setPaymentsOpen] = useState(false);

  // The stacked touring-party drawer (crew / entourage / performing members of a saved
  // booking). While it's open we gate this drawer's ⌘S shortcut, same as payments.
  const [partyOpen, setPartyOpen] = useState(false);

  // Count of NAMED party members (event guests linked to this booking) — for the
  // total-party-size rollup next to the manual "un-named heads" count. Only meaningful
  // once the booking is saved.
  const [namedPartyCount, setNamedPartyCount] = useState<number | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  const loadPartyCount = useCallback(async () => {
    if (!isEditMode || !recordId) {
      setNamedPartyCount(null);

      return;
    }

    const result = await listAllPages(EventGuestService.list, { eventId: [eventId], eventArtistId: [recordId] });

    if (result.didSucceed() && Array.isArray(result.data)) {
      setNamedPartyCount(result.data.length);
    }
  }, [isEditMode, recordId, eventId]);

  // Re-fetch the loaded booking so the budget-derived paid / remaining refresh after a
  // payment change in the stacked drawer (the fee's mirror budget item resolves live).
  const refreshBudgetItem = useCallback(async () => {
    if (!isEditMode || !recordId || !loadFnRef.current) {
      return;
    }

    // Reload the booking but push ONLY the refreshed budget line into its own state,
    // so paid / remaining update without re-seeding (and clobbering) the form.
    const result = await loadFnRef.current(recordId);

    if (result.didSucceed()) {
      const fresh = result.getDataFirst() as EventArtist;
      setBudgetItem(fresh?.budgetItem ?? null);
    }
  }, [isEditMode, recordId]);

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          const fresh = result.getDataFirst() as EventArtist;
          setRecord(fresh);
          setBudgetItem(fresh?.budgetItem ?? null);
          setLoadError(null);
        } else {
          setRecord(null);
          setBudgetItem(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setBudgetItem(null);
      setIsLoadingRecord(false);
      setLoadError(null);
      setPaymentsOpen(false);
      setPartyOpen(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  useEffect(() => {
    if (opened && isEditMode && recordId) {
      loadPartyCount();
    } else if (!opened) {
      setNamedPartyCount(null);
    }
  }, [opened, isEditMode, recordId, loadPartyCount]);

  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record, timezone) : getInitialFormData(defaultSlotId);
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, timezone, defaultSlotId]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Picking a catalog act copies its name (when the name is still empty), its deal
  // defaults, and links artistId. The BE re-snapshots name/photo/deal on create.
  // The act picked in THIS session — only to say when its catalog currency differs from the event's.
  const [pickedArtist, setPickedArtist] = useState<Artist | null>(null);

  const handleArtist = useCallback((artist: Artist | null) => {
    setPickedArtist(artist);
    setFormData((prev) => ({
      ...prev,
      artistId: artist?.id ?? null,
      name: prev.name.trim() || artist?.name || "",
      dealType: prev.dealType !== EVENT_ARTIST_DEAL_TYPE.NONE ? prev.dealType : artist?.dealType || prev.dealType,
      amount: prev.amount == null && artist?.defaultAmount != null ? artist.defaultAmount : prev.amount,
    }));
  }, []);

  const billingOptions = useMemo(
    () => ARTIST_BILLING_VALUES.map((v) => ({ value: v, label: t(`artists:billing.${v}`, { defaultValue: ARTIST_BILLING_LABELS[v] }) })),
    [t]
  );

  const bookingStatusOptions = useMemo(
    () => ARTIST_BOOKING_STATUS_VALUES.map((v) => ({ value: v, label: t(`artists:bookingStatus.${v}`, { defaultValue: ARTIST_BOOKING_STATUS_LABELS[v] }) })),
    [t]
  );

  const dealTypeOptions = useMemo(
    () => EVENT_ARTIST_DEAL_TYPE_VALUES.map((v) => ({ value: v, label: t(`artists:dealType.${v}`, { defaultValue: EVENT_ARTIST_DEAL_TYPE_LABELS[v] }) })),
    [t]
  );


  // A date row added but not picked yet: Save waits for it (a presence check — the server checks the rest).
  const missingDate = withDates && formData.performances.some((p) => !p.eventTimeSlotId);

  const isHeld = formData.bookingStatus === ARTIST_BOOKING_STATUS.HELD;
  const showPercentage = ARTIST_DEAL_TYPES_WITH_PERCENTAGE.includes(formData.dealType);
  // #235 — what the SAVED door deal pays on the door so far. Only while the deal on screen is the one saved: a changed
  // deal has not been priced yet, and a number about the old one would read as the new one's.
  const doorNow =
    record?.dealDoorAmount != null &&
    record?.dealPayoutAmount != null &&
    showPercentage &&
    formData.dealType === record.dealType &&
    Number(formData.percentage) === Number(record.percentage ?? "") &&
    Number(formData.amount || 0) === Number(record.amount ?? 0)
      ? { door: record.dealDoorAmount, payout: record.dealPayoutAmount }
      : null;

  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const files: EventArtistFileInput[] = formData.files.map((f) => ({
      id: f.id,
      fileKind: f.fileKind ?? null,
      uploadedFileId: f.uploadedFileId ?? null,
      fileName: f.fileName ?? null,
      contentType: f.contentType ?? null,
      fileSize: f.fileSize ?? null,
    }));

    const submitData: EventArtistInput = {
      eventId: isEditMode ? undefined : eventId,
      artistId: formData.artistId,
      name: formData.name.trim() || null,
      mainImageUploadedFileId: formData.photo?.uploadedFileId ?? null,
      blurb: formData.blurb.trim() || null,
      billing: formData.billing || null,
      billingIndex: formData.billingIndex === "" ? null : Number(formData.billingIndex),
      bookingStatus: formData.bookingStatus || null,
      holdRank: isHeld && formData.holdRank !== "" ? Number(formData.holdRank) : null,
      holdExpiresAt: isHeld ? formData.holdExpiresAt : null,
      contractSigned: formData.contractSigned,
      depositPaid: formData.depositPaid,
      advanceComplete: formData.advanceComplete,
      dealType: formData.dealType || null,
      amount: formData.amount,
      percentage: showPercentage && formData.percentage !== "" ? Number(formData.percentage) : null,
      depositAmount: formData.depositAmount,
      trackInBudget: formData.trackInBudget,
      entourageCount: formData.entourageCount === "" ? null : Number(formData.entourageCount),
      notes: formData.notes.trim() || null,
      files,
      // #165 round 2 — only when the dates are on screen; otherwise they are left as they are.
      performances: withDates
        ? formData.performances.map((p) => ({
            eventTimeSlotId: p.eventTimeSlotId,
            ...setInstants(p, dates?.find((d) => d.id === p.eventTimeSlotId)?.startsAt ?? null, timezone),
            mapElementId: p.mapElementId || null,
          }))
        : undefined,
      status: formData.status,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as EventArtist[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, eventId, isHeld, showPercentage, createFn, updateFn, onClose, onCreated, onUpdated, withDates, dates, timezone]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord && !missingDate && !paymentsOpen && !partyOpen,
    enabled: opened && !paymentsOpen && !partyOpen,
  });

  const drawerTitle = isEditMode
    ? t("artists:booking.editTitle", { defaultValue: "Edit booking" })
    : t("artists:booking.addTitle", { defaultValue: "Book an act" });

  return (
    <>
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord || missingDate}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <EditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          {/* ── Act ── */}
          <ArtistSelect
            value={formData.artistId}
            onChange={handleArtist}
            eventId={eventId}
            fallbackLabel={record?.name ?? null}
            label={t("artists:booking.fields.artist.label", { defaultValue: "Catalog act (optional)" })}
            description={t("artists:booking.fields.artist.description", {
              defaultValue: "Pick from your roster — or leave blank and just type a name below for a one-off act.",
            })}
            error={fieldErrors.artistId}
          />

          <TextInput
            data-autofocus
            label={t("artists:booking.fields.name.label", { defaultValue: "Act name" })}
            description={t("artists:booking.fields.name.description", {
              defaultValue: "Shown on your lineup. Filled from the catalog act; edit for an override or an ad-hoc act.",
            })}
            placeholder={t("artists:booking.fields.name.placeholder", { defaultValue: "e.g. The Midnight" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            size="md"
          />

          <MediaImageField
            intent="content-image"
            allowUrl={false}
            value={formData.photo}
            onChange={(photo) => setField("photo", photo)}
            label={t("artists:booking.fields.photo.label", { defaultValue: "Photo (optional)" })}
            description={t("artists:booking.fields.photo.description", {
              defaultValue: "Override for the lineup. Falls back to the catalog act's image.",
            })}
            addLabel={t("artists:booking.fields.photo.add", { defaultValue: "Add photo" })}
            aspect={1}
            error={fieldErrors.mainImageUploadedFileId}
            disabled={isSubmitting}
          />

          <Textarea
            label={t("artists:booking.fields.blurb.label", { defaultValue: "Lineup blurb" })}
            description={t("artists:booking.fields.blurb.description", { defaultValue: "Public text shown on your event website." })}
            value={formData.blurb}
            onChange={(e) => setField("blurb", e.currentTarget.value)}
            error={fieldErrors.blurb}
            autosize
            minRows={2}
            maxRows={5}
            size="md"
          />

          {/* ── Lineup ── */}
          <Divider label={t("artists:booking.sections.lineup", { defaultValue: "Lineup" })} labelPosition="left" />

          <Group grow align="flex-start">
            <SelectField
              label={t("artists:booking.fields.billing.label", { defaultValue: "Billing" })}
              placeholder={t("artists:booking.fields.billing.placeholder", { defaultValue: "Select billing" })}
              data={billingOptions}
              value={formData.billing || null}
              onChange={(_name, v) => setField("billing", v ?? "")}
              error={fieldErrors.billing}
              clearable
              size="md"
            />
            <NumberInput
              label={t("artists:booking.fields.billingIndex.label", { defaultValue: "Lineup order" })}
              description={t("artists:booking.fields.billingIndex.description", { defaultValue: "Lower shows first." })}
              placeholder="1"
              value={formData.billingIndex}
              onChange={(v) => setField("billingIndex", v === "" ? "" : (v as number))}
              error={fieldErrors.billingIndex}
              min={0}
              step={1}
              size="md"
            />
          </Group>

          {/* Whether the act is on the PUBLIC lineup (the record's status: shown = active, hidden = disabled) — a switch
              that says what it does, not a second "Status" beside the booking status. */}
          <Switch
            label={t("artists:booking.fields.showOnLineup.label", { defaultValue: "Show on the event's lineup" })}
            description={t("artists:booking.fields.showOnLineup.description", {
              defaultValue: "Off keeps the booking for your team only — the website's lineup and schedule leave it out.",
            })}
            checked={formData.status === STATUSES.ACTIVE}
            onChange={(e) => {
              const shown = e.currentTarget.checked;

              setField("status", shown ? STATUSES.ACTIVE : STATUSES.DISABLED);
            }}
            error={fieldErrors.status}
            size="md"
          />

          {/* ── Dates & set times (#165 round 2) ── */}
          {withDates && (
            <>
              <Divider
                label={
                  isRecurring
                    ? t("artists:booking.sections.dates", { defaultValue: "Dates & set times" })
                    : t("artists:booking.sections.sessions", { defaultValue: "Sessions & set times" })
                }
                labelPosition="left"
              />
              <EventArtistDatesEditor
                dates={dates ?? []}
                timezone={timezone}
                isRecurring={isRecurring}
                stages={stages}
                value={formData.performances}
                onChange={(rows) => setField("performances", rows)}
                disabled={isSubmitting}
                error={
                  fieldErrors.performances ||
                  (missingDate ? t("artists:booking.dates.missing", { defaultValue: "Pick the date for each set, or take the row off." }) : undefined)
                }
              />
            </>
          )}

          {/* ── Status ── */}
          <Divider label={t("artists:booking.sections.status", { defaultValue: "Booking" })} labelPosition="left" />

          {/* ⓘ THE booking's status (inquiry → confirmed …). Whether the act shows on the website is the switch in Lineup —
              two controls both called "Status" read as one (owner, 2026-10-16). */}
          <SelectField
            label={t("artists:booking.fields.bookingStatus.label", { defaultValue: "Booking status" })}
            data={bookingStatusOptions}
            value={formData.bookingStatus || null}
            onChange={(_name, v) => setField("bookingStatus", v ?? ARTIST_BOOKING_STATUS.INQUIRY)}
            error={fieldErrors.bookingStatus}
            allowDeselect={false}
            size="md"
          />

          {isHeld && (
            <Group grow align="flex-start">
              <NumberInput
                label={t("artists:booking.fields.holdRank.label", { defaultValue: "Hold rank" })}
                description={t("artists:booking.fields.holdRank.description", { defaultValue: "1 = first refusal." })}
                placeholder="1"
                value={formData.holdRank}
                onChange={(v) => setField("holdRank", v === "" ? "" : (v as number))}
                error={fieldErrors.holdRank}
                min={1}
                step={1}
                size="md"
              />
              <DatePickerInput
                label={t("artists:booking.fields.holdExpiresAt.label", { defaultValue: "Hold expires" })}
                placeholder={t("artists:booking.fields.holdExpiresAt.placeholder", { defaultValue: "Pick a date" })}
                value={formData.holdExpiresAt}
                onChange={(v) => setField("holdExpiresAt", v)}
                error={fieldErrors.holdExpiresAt}
                clearable
                size="md"
              />
            </Group>
          )}

          <Input.Wrapper label={t("artists:booking.fields.milestones.label", { defaultValue: "Milestones" })}>
            <Stack gap="xs" mt={6}>
              <Checkbox
                label={t("artists:booking.fields.contractSigned.label", { defaultValue: "Contract signed" })}
                checked={formData.contractSigned}
                onChange={(e) => setField("contractSigned", e.currentTarget.checked)}
                error={fieldErrors.contractSigned}
                size="md"
              />
              <Checkbox
                label={t("artists:booking.fields.depositPaid.label", { defaultValue: "Deposit paid" })}
                checked={formData.depositPaid}
                onChange={(e) => setField("depositPaid", e.currentTarget.checked)}
                error={fieldErrors.depositPaid}
                size="md"
              />
              <Checkbox
                label={t("artists:booking.fields.advanceComplete.label", { defaultValue: "Advance complete" })}
                checked={formData.advanceComplete}
                onChange={(e) => setField("advanceComplete", e.currentTarget.checked)}
                error={fieldErrors.advanceComplete}
                size="md"
              />
            </Stack>
          </Input.Wrapper>

          {/* ── Money ── */}
          <Divider label={t("artists:booking.sections.money", { defaultValue: "Money" })} labelPosition="left" />

          <Group grow align="flex-start">
            <SelectField
              label={t("artists:booking.fields.dealType.label", { defaultValue: "Deal type" })}
              data={dealTypeOptions}
              value={formData.dealType || null}
              onChange={(_name, v) => setField("dealType", v ?? EVENT_ARTIST_DEAL_TYPE.NONE)}
              error={fieldErrors.dealType}
              allowDeselect={false}
              size="md"
            />
          </Group>

          <Group grow align="flex-start">
            <MoneyInput
              label={t("artists:booking.fields.amount.label", { defaultValue: "Guarantee / fee" })}
              value={formData.amount}
              onChange={(v) => setField("amount", v)}
              currency={eventCurrency}
              error={fieldErrors.amount}
              size="md"
            />
          </Group>

          <CatalogCurrencyWarning sourceCurrencyCode={pickedArtist?.currencyCode} sourceLabel={t("artists:booking.currencySource", { defaultValue: "This act" })} />

          {doorNow && (
            <Text size="md" c="dimmed">
              {t("artists:booking.doorNow", {
                defaultValue: "Door so far {{door}} — this deal pays {{payout}}. It follows ticket sales until the show.",
                door: formatCurrency(doorNow.door, eventCurrency || undefined),
                payout: formatCurrency(doorNow.payout, eventCurrency || undefined),
              })}
            </Text>
          )}

          {showPercentage && (
            <NumberInput
              label={t("artists:booking.fields.percentage.label", { defaultValue: "Percentage (%)" })}
              description={t("artists:booking.fields.percentage.description", {
                defaultValue: "Share of the door: admission tickets for the act's dates, after refunds, sales tax and fees guests paid.",
              })}
              placeholder="0"
              value={formData.percentage}
              onChange={(v) => setField("percentage", v === "" ? "" : (v as number))}
              error={fieldErrors.percentage}
              min={0}
              max={100}
              decimalScale={2}
              step={1}
              size="md"
            />
          )}

          <MoneyInput
            label={t("artists:booking.fields.depositAmount.label", { defaultValue: "Deposit" })}
            value={formData.depositAmount}
            onChange={(v) => setField("depositAmount", v)}
            currency={eventCurrency}
            error={fieldErrors.depositAmount}
            size="md"
          />

          <Switch
            label={t("artists:booking.fields.trackInBudget.label", { defaultValue: "Track this cost in the event budget" })}
            description={t("artists:booking.fields.trackInBudget.description", {
              defaultValue: showPercentage
                ? "Adds what the deal pays on ticket sales so far as an event budget expense; record payments there."
                : "Adds the fee as an event budget expense; record payments there.",
            })}
            checked={formData.trackInBudget}
            onChange={(e) => setField("trackInBudget", e.currentTarget.checked)}
            error={fieldErrors.trackInBudget}
            size="md"
          />

          {isEditMode && formData.trackInBudget && budgetItem ? (
            <Card withBorder radius="md" padding="sm">
              <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
                <SimpleGrid cols={2} spacing="xl">
                  <Box>
                    <Text size="xs" c="dimmed">
                      {t("artists:booking.payments.paid", { defaultValue: "Paid" })}
                    </Text>
                    <Text size="md" fw={700} c="teal">
                      {formatCurrency(budgetItem.paidAmount ?? 0, budgetItem.currencyCode || eventCurrency || undefined)}
                    </Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">
                      {t("artists:booking.payments.remaining", { defaultValue: "Remaining" })}
                    </Text>
                    <Text size="md" fw={700} c={(budgetItem.remainingAmount ?? 0) > 0 ? "red" : undefined}>
                      {formatCurrency(budgetItem.remainingAmount ?? 0, budgetItem.currencyCode || eventCurrency || undefined)}
                    </Text>
                  </Box>
                </SimpleGrid>
                <Button
                  variant="light"
                  leftSection={<AppIcon iconName="Cash" size={16} />}
                  onClick={() => setPaymentsOpen(true)}
                >
                  {t("artists:booking.payments.open", { defaultValue: "Payments" })}
                </Button>
              </Group>
            </Card>
          ) : formData.trackInBudget ? (
            <Text size="sm" c="dimmed">
              {t("artists:booking.payments.saveHint", { defaultValue: "Save the booking to record payments." })}
            </Text>
          ) : null}

          {/* ── Party ── */}
          <Divider label={t("artists:booking.sections.party", { defaultValue: "Party" })} labelPosition="left" />

          {isEditMode && record?.id ? (
            <Card withBorder radius="md" padding="sm">
              <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
                <Box>
                  <Text size="md" fw={600}>
                    {t("artists:booking.party.title", { defaultValue: "Touring party" })}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {namedPartyCount != null
                      ? t("artists:booking.party.count", {
                          defaultValue: "{{count}} named member",
                          defaultValue_plural: "{{count}} named members",
                          count: namedPartyCount,
                        })
                      : t("artists:booking.party.manageHint", {
                          defaultValue: "Crew, entourage, and performing members travelling with the act.",
                        })}
                  </Text>
                </Box>
                <Button
                  variant="light"
                  leftSection={<AppIcon iconName="Users" size={16} />}
                  onClick={() => setPartyOpen(true)}
                >
                  {t("artists:booking.party.manage", { defaultValue: "Manage party" })}
                </Button>
              </Group>
            </Card>
          ) : (
            <Text size="sm" c="dimmed">
              {t("artists:booking.party.saveHint", { defaultValue: "Save the booking to add its party." })}
            </Text>
          )}

          <NumberInput
            label={t("artists:booking.fields.entourageCount.label", { defaultValue: "Additional un-named heads" })}
            description={t("artists:booking.fields.entourageCount.description", {
              defaultValue: "A manual extra count for people you don't name individually above. Total party size = named members + this number.",
            })}
            placeholder="0"
            value={formData.entourageCount}
            onChange={(v) => setField("entourageCount", v === "" ? "" : (v as number))}
            error={fieldErrors.entourageCount}
            min={0}
            step={1}
            size="md"
          />

          {isEditMode && namedPartyCount != null ? (
            <Text size="sm" c="dimmed">
              {t("artists:booking.party.total", {
                defaultValue: "Total party size: {{total}} ({{named}} named + {{extra}} un-named)",
                total: namedPartyCount + (formData.entourageCount === "" ? 0 : Number(formData.entourageCount)),
                named: namedPartyCount,
                extra: formData.entourageCount === "" ? 0 : Number(formData.entourageCount),
              })}
            </Text>
          ) : null}

          {/* ── Attachments ── */}
          <Divider label={t("artists:booking.sections.attachments", { defaultValue: "Attachments" })} labelPosition="left" />

          <Box>
            <EventArtistFilesEditor
              value={formData.files}
              onChange={(files) => setField("files", files)}
              disabled={isSubmitting}
              error={fieldErrors.files}
            />
          </Box>

          {/* ── Notes ── */}
          <Divider label={t("artists:booking.sections.notes", { defaultValue: "Notes" })} labelPosition="left" />

          <Textarea
            label={t("artists:booking.fields.notes.label", { defaultValue: "Internal notes" })}
            description={t("artists:booking.fields.notes.description", { defaultValue: "Only visible to your team." })}
            value={formData.notes}
            onChange={(e) => setField("notes", e.currentTarget.value)}
            error={fieldErrors.notes}
            autosize
            minRows={2}
            maxRows={6}
            size="md"
          />

        </Stack>
      )}
    </AppDrawer>

      {budgetItem && (
        <BudgetPaymentsDrawer
          opened={paymentsOpen}
          onClose={() => setPaymentsOpen(false)}
          item={budgetItem}
          eventId={eventId}
          onChanged={refreshBudgetItem}
        />
      )}

      {isEditMode && record?.id && (
        <TouringPartyDrawer
          opened={partyOpen}
          onClose={() => setPartyOpen(false)}
          eventId={eventId}
          eventArtistId={record.id}
          actName={record.name}
          onChanged={loadPartyCount}
        />
      )}
    </>
  );
};
